import cuid from 'cuid';
import { Map, List } from 'immutable';
import { curry, filter, findIndex, forEach, map, reduce, values } from 'ramda';
import createReducer from './utils/createReducer';
import { get, multi, set } from './sceneGraph';
import tweenFunctions from './utils/tweenFunctions';
import { LOAD_SCENE, CLEAR_SCENE } from './actionTypes';
import { ActionTypes } from '../store/createStore';
import { find as sceneFind, getAll } from './scene';
import { initPhysics, updatePhysics } from './utils/physics';

const SET_TIME = 'SET_TIME';
const SET_FRAME_TIME = 'SET_FRAME_TIME';
const START_PLAYING = 'START_PLAYING';
const STOP_PLAYING = 'STOP_PLAYING';
const UPDATE_TRACK = 'UPDATE_TRACK';
const UPDATE_ANIMATION = 'UPDATE_ANIMATION';
const REMOVE_ANIMATION = 'REMOVE_ANIMATION';
const QUEUE_ANIMATION = 'QUEUE_ANIMATION';
const QUEUE_TIMELINE_ANIMATION = 'QUEUE_TIMELINE_ANIMATION';
const ADD_CLIP = 'ADD_CLIP';
const INIT_PHYSICS = 'INIT_PHYSICS';


/**
 * Animation module.
 *
 * This module controls the animation happening. The properties of an
 * animation are [defined here in the Animation type](global.html#Animation).
 *
 * Animations can be queued dynamically with [`queueAnimation`](module-animation.html#.queueAnimation)
 * Or, to play existing clips, queue a clip for playing with [`queueClip`](module-animation.html#.queueClip).
 *
 * @module animation
 * @public
 *
 */

function newInitialState() {
  return Map({
    time: 0,
    startTime: null,
    playing: false,
    autoplay: false,
    frameTime: 0,
    animations: List(),
    clips: Map(),
    timelineAnimationIndex: -1,
    dynamicsWorld: null,
    dynamicBodies: {},
    kinematicBodies: {},
  });
}

const reducer = createReducer(newInitialState(), {
  [ActionTypes.INIT](state) {
    return newInitialState();
  },

  [SET_TIME](state, { time, startTime }) {
    return state.set('time', time).set('startTime', startTime);
  },

  [SET_FRAME_TIME](state, frameTime) {
    const currentFrameTime = state.get('frameTime');
    // Apply a smoothing moving average to frameTime
    return state.set('frameTime', currentFrameTime + (frameTime - currentFrameTime) * 0.1);
  },

  [START_PLAYING](state, autoplay) {
    return state.merge({ playing: true, autoplay: !!autoplay });
  },

  [STOP_PLAYING](state) {
    return state.set('playing', false);
  },

  [UPDATE_TRACK](state, { path, attrs }) {
    const anim = state.getIn(['animations', path[0]]);
    const track = anim && anim.tracks[path[1]];
    if (track) Object.assign(track, attrs);
    return state;
  },

  [UPDATE_ANIMATION](state, { index, attrs }) {
    const anim = state.getIn(['animations', index]);
    if (anim) Object.assign(anim, attrs);
    return state;
  },

  [REMOVE_ANIMATION](state, idx) {
    let newState = state;
    if (idx === state.get('timelineAnimationIndex')) newState = newState.set('timelineAnimationIndex', -1);
    return newState.deleteIn(['animations', idx]);
  },

  [QUEUE_ANIMATION](state, animation) {
    return state.updateIn(['animations'], List(), list => list.push(animation));
  },

  [QUEUE_TIMELINE_ANIMATION](state, animation) {
    return state.set('timelineAnimationIndex', state.get('animations').size - 1);
  },

  [ADD_CLIP](state, { clip, id }) {
    return state.setIn(['clips', id], Map(clip).set('id', id));
  },

  [LOAD_SCENE](state, { clips }) {
    return clips ? state.mergeIn(['clips'], clips) : state;
  },

  [CLEAR_SCENE](state) {
    return initialState;
  },

  [INIT_PHYSICS](state, { dynamicsWorld, dynamicBodies, kinematicBodies }) {
    return state.set('dynamicsWorld', dynamicsWorld).set('dynamicBodies', dynamicBodies).set('kinematicBodies', kinematicBodies);
  },
});

export function setFrameTime(frameTime) {
  return { type: SET_FRAME_TIME, payload: frameTime };
};

/**
 * Return the current animation time.
 * @returns {Number}
 * @public
 *
 */
export function getTime(store) {
  return store.getIn(['animation', 'time']);
};

/**
 * Set the current animation time.
 * @public
 * @param {Number} time in seconds
 */
export function setTime(time) {
  return (store) => {
    const startTime = new Date();
    const delta = time - store.getIn(['animation', 'time']);
    const dynamicsWorld = store.getIn(['animation', 'dynamicsWorld']);

    if (store.getIn(['animation', 'animations']).size) {
      let offsetIdx = 0;
      store.getIn(['animation', 'animations']).forEach((anim, animIdx) => {
        const keepAnimation = updateAnimation(store, delta, time, anim, animIdx-offsetIdx);
        if(!keepAnimation) offsetIdx++;
      });
    } else {
      if (store.getIn(['animation', 'autoplay'])) {
        store.dispatch(stopPlaying());
      }
    }

    if (dynamicsWorld) {
      updatePhysics(store, delta, time, dynamicsWorld,
        store.getIn(['animation', 'dynamicBodies']),
        store.getIn(['animation', 'kinematicBodies'])
      );
    }

    store.dispatch({ type: SET_TIME, payload: { time, startTime } });
  };
};

function getKeyframeValueFor(t, keyframes, tween, currentValue) {
  const { vs, ts } = keyframes;

  if (!ts.length) return currentValue;

  const idx = findIndex((kt) => t <= kt, ts);
  if (idx === -1) return vs[vs.length - 1];

  // FIXME: Handle keyframe that hasn't started yet
  if (idx === 0) return vs[0];

  //console.log('tween?', frame1.v, frame2.v, frame2.t - frame1.t);
  const v = tween(t - ts[idx - 1], vs[idx - 1], vs[idx], ts[idx] - ts[idx - 1]);
  //console.log('getkeyframe value?', t, idx, v);
  return v;
}

function getKeyframeValue(t, keyframes, tween, currentValue) {
  //console.log('get keyframe value: ', t, keyframes, tween);
  if (keyframes.ts) return getKeyframeValueFor(t, keyframes, tween, currentValue);
  const result = {};
  const keys = Object.keys(keyframes);
  for (var i = 0; i < keys.length; i++) {
    result[keys[i]] = getKeyframeValueFor(t, keyframes[keys[i]], tween, currentValue[keys[i]]);
  }

  return result;
};

function updateAnimation(store, delta, time, animation, animationIdx) {
  //console.log('updateAnimation', delta, time, animation.name, animationIdx);

  const { tracks, first, iterations, duration, easingFn, easingDuration, playSpeed } = animation;
  let { easingTime } = animation;
  const animationStart = animation.start;
  const trackSet = animation.set;

  let animationTime = time;

  const isEasing = first && time < (animationStart + easingDuration) && time > animationStart;
  if (isEasing) {
    easingTime += delta * easingFn(time - animationStart, 0, 1, easingDuration);
    animationTime = animationStart + easingTime;
  } else if (easingTime) {
    animationTime = time - easingDuration + easingTime;
  }

  const reverse = playSpeed < 0;

  if (playSpeed !== 1) {
    animationTime = (animationTime - animationStart) * playSpeed + animationStart;
    if (reverse) animationTime = animationTime + duration;
  }

  let inProgress = false;
  let ops = [];

  var s = new Date();
  let track;
  for (var trackIdx = 0; trackIdx < tracks.length; trackIdx++) {
    track = tracks[trackIdx];
    const start = track.start + animationStart;
    const end = track.end + animationStart;
    if (reverse ? animationTime > end : animationTime < start) {
      inProgress = true;
      continue;
    }

    const endValue = track.value;
    const { tween, path, keyframes, duration, currentValue, trackSet } = track;
    let startValue = track.startValue;

    // TODO: this should be more efficient
    //let currentValue = endValue; //get(store, path);

    if (startValue == null && !keyframes) {
      startValue = get(store, path);
      store.dispatch({ type: UPDATE_TRACK, payload: { path: [animationIdx, trackIdx], attrs: { startValue } } });
    }

    let v = keyframes ?
      getKeyframeValue(animationTime - start, keyframes, tween, currentValue) :
      (animationTime > end ? endValue : tween(animationTime - start, startValue, endValue, duration));

    const op = trackSet(path, v);
    if (op) ops.push(op);

    if (reverse ? animationTime >= start : animationTime <= end) inProgress = true;
  }

  //console.log('dispatch animations',ops.length, 'in',  new Date() - s);
  if (ops.length) store.dispatch(multi(ops));

  if (inProgress) {
    if (isEasing) {
      store.dispatch({ type: UPDATE_ANIMATION, payload: { index: animationIdx, attrs: {
        easingTime,
      }, }, });
    }
  } else {
    if (iterations > 1) {
      store.dispatch({ type: UPDATE_ANIMATION, payload: { index: animationIdx, attrs: {
        first: false, easingTime: 0, start: time, iterations: iterations - 1,
      }, }, });
    } else {
      if (animation.onEnd) animation.onEnd();

      if (animationIdx !== store.getIn(['animation', 'timelineAnimationIndex'])) {
        store.dispatch({ type: REMOVE_ANIMATION, payload: animationIdx });
        return false;
      } else {
        store.dispatch({ type: UPDATE_ANIMATION, payload: {
          index: animationIdx, attrs: { start: time },
        }, });
        store.dispatch(stopPlaying());
      }
    }
  }
  return true;
};

/**
 * Are we currently playing
 * @returns {Boolean}
 */
export function isPlaying(store) {
  return store.getIn(['animation', 'playing']);
};

/**
 * Start time
 * @public
 */
export function startPlaying(options={}) {
  return (store) => {
    store.dispatch({ type: START_PLAYING, payload: options.autoplay });
    if (typeof Ammo !== 'undefined' && !store.getIn(['animation', 'dynamicsWorld'])) {
      store.dispatch({ type: INIT_PHYSICS, payload: initPhysics(store) });
    }
    store.dispatch(setTime(store.getIn(['animation', 'time'])));
  };
};

/*
 * If we are currently playing, increment time
 */
export function incrementTime() {
  return (store) => {
    if (store.getIn(['animation', 'playing'])) {
      const startTime = store.getIn(['animation', 'startTime']);
      const time = store.getIn(['animation', 'time']);
      const delta = startTime ? (new Date() - startTime) : 0;
      store.dispatch(setTime(time + delta));
    }
  };
};

function getLastKeyframeTime(keyframes) {
  if (keyframes.ts) return keyframes.ts.length ? keyframes.ts[keyframes.ts.length - 1] : 0;
  let lastTime = 0;
  let times;
  const keys = Object.keys(keyframes);
  for (var i = 0; i < keys.length; i++) {
    times = keyframes[keys[i]].ts;
    lastTime = times.length ? Math.max(times[times.length - 1], lastTime) : lastTime;
  }

  return lastTime;
};

/**
 * Stop time
 * @public
 */
export function stopPlaying() {
  return { type: STOP_PLAYING };
};

function formatTrack(store, sceneTime, attrs) {
  const id = cuid();
  let path = Array.isArray(attrs.path) ? attrs.path : attrs.path && sceneFind(store, attrs.path);
  if (!path && attrs.path && attrs.path.id) {
    delete attrs.path.id;
    path = sceneFind(store, attrs.path)
  }
  if (!path && !attrs.set) {
    console.log('path is not defined for track', attrs);
    return null;
  }

  const start = attrs.start || 0;
  const value = attrs.value;
  const tween = attrs.tween || getTweenFunction(value, attrs.easing || 'linear');
  const trackSet = attrs.set || set;
  const keyframes = attrs.keyframes;
  const duration = keyframes ? getLastKeyframeTime(keyframes) : (attrs.duration || 0);
  const end = duration + start;
  const startValue = duration === 0 ? value : attrs.startValue;
  const currentValue = path && get(store, path);
  return { path, start, end, value, duration, tween, startValue, keyframes, currentValue, trackSet };
};

/**
 * Queue a clip to be played
 *
 * @public
 * @param {String} clipId the id of the clip to be queued.
 * @param {Object} attrs Attrs to override the clip settings.
 * @param {Object} options
 * @param {Boolean} options.useTimeline Display the clip in the timeline
 * @returns {Promise}
 *
 * @example
 * var clips = animation.getClips();
 * animation.queueClip(clips[0].id, {autoplay: true});
 *
 */
export function queueClip(clipId, attrs, options={}) {
  return (store) => {
    var clip = store.getIn(['animation', 'clips', clipId]);
    if (clip) {
      store.dispatch(queueAnimation(Object.assign({}, clip.toJS(), attrs)));
      if (options.useTimeline) store.dispatch({ type: QUEUE_TIMELINE_ANIMATION });
    }
  };
};


/**
 * Queue an animation
 *
 * @public
 * @param {Animation} attrs the animation object
 * @returns {Promise}
 */
export function queueAnimation(attrs) {
  return (store) => {
    const sceneTime = store.getIn(['animation', 'time']);

    const name = attrs.name || 'Animation';
    const iterations = attrs.iterations || 1;
    const start = attrs.start || sceneTime;
    const easing = attrs.easing || 'linear';
    const easingFn = getTweenFunction(1, easing);
    const easingDuration = attrs.easingDuration || 0;
    const playSpeed = typeof attrs.playSpeed !== 'undefined' ? attrs.playSpeed : 1;
    const trackList = filter((t) => !!t, map((attrs) => formatTrack(store, sceneTime, attrs), attrs.tracks));
    const duration = reduce((lastEnd, track) => Math.max(lastEnd, track.end), 0, trackList);
    const onEnd = attrs.onEnd || null;

    //let end = 0;
    //forEach((t) => { if (t.get('end') > end) end = t.get('end'); }, trackList);

    store.dispatch({
      type: QUEUE_ANIMATION,
      payload: {
        name,
        start,
        easingTime: 0,
        first: true,
        easing,
        easingFn,
        easingDuration,
        iterations,
        tracks: trackList,
        onEnd,
        playSpeed,
        duration,
      },
    });

    if (attrs.autoplay && !isPlaying(store)) {
      store.dispatch(startPlaying({ autoplay: true }));
    }

    return Promise.resolve(true);
  };
};

export function updateAnimationAttrs(name, attrs){
  return (store) => {
    const index = findAnimationIndex(name, store);
    return store.dispatch({ type: UPDATE_ANIMATION, payload:{ index, attrs }});
  };
}

export function findAnimationIndex(name, store) {
  return store.getIn(['animation', 'animations']).findIndex((anim) => anim.name === name);
};

/**
 * Remove an animation
 *
 * @public
 * @param {String} name the name of the animation
 * @returns {Promise}
 */
export function removeAnimation(name) {
  return (store) => {
    let animationIdx = findAnimationIndex(name, store);
    if (animationIdx !== -1) {
      return store.dispatch({ type: REMOVE_ANIMATION, payload: animationIdx });
    }
  };

};

/**
 * Add a clip to the store.
 * @param {Object} clip
 * @param {String} id
 * @returns {Promise}
 */
export function addClip(clip, id) {
  return { type: ADD_CLIP, payload: { clip, id } };
};

/**
 * Return a list of clips available.
 *
 * @public
 * @returns {Clip[]}
 */
export function getClips(store) {
  return values(store.getIn(['animation', 'clips']).toJS());
};

function getTweenFunction(value, easing) {
  const tweenFn = tweenFunctions[easing];
  if (!tweenFn) throw new Error('Unknown easing function: ' + easing);

  if (typeof value !== 'object') return tweenFn;

  const keys = Object.keys(value);

  return (t, b, c, d) => {
    const result = {};
    for (var i = 0; i < keys.length; i++) {
      const key = keys[i];
      result[key] = tweenFn(t, b[key], c[key], d);
    }

    return result;
  };
};

export function getTimelineAnimation(store) {
  const index = store.getIn(['animation', 'timelineAnimationIndex']);
  if (index < 0) return null;
  return store.getIn(['animation', 'animations', index]);
}

const publicApi = {
  reducer,
  actions: { setTime, startPlaying, stopPlaying, removeAnimation, queueAnimation,
    addClip, queueClip, updateAnimationAttrs
  },
  selectors: { getTime, getClips, getTimelineAnimation, isPlaying },
};

export default publicApi;



// WEBPACK FOOTER //
// ./src/modules/animation.js